import { useLayoutEffect, useRef } from 'react'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { useSharedSurface } from '../Shared/SharedSurfaceContext'

// The menu tab-entry leaf (relocated from Shared/Shared.tsx into the menu
// skeleton next to menuModel.ts; Shared re-exports it for old import paths).

export function NavItem({
  label,
  className,
  mode,
  isActive = false,
  isLoading = false,
  loadingCount = 0,
}: {
  label: string
  className: string
  mode: DeskViewMode
  isActive?: boolean
  isLoading?: boolean
  loadingCount?: number
}) {
  const { skin } = useSharedSurface()
  const itemRef = useRef<HTMLLIElement | null>(null)
  const idleWidth = useRef<number | null>(null)

  // While a tab loads it shows "+N" INSIDE its own label — no extra node — and
  // must not resize. The idle width is therefore remembered on every idle
  // layout, so the loading render can pin it before the text swaps.
  // (Ref, not state: measuring must not schedule another render.)
  useLayoutEffect(() => {
    // getBoundingClientRect, not offsetWidth: the latter rounds to whole pixels
    // and the tab would jump by a fraction of a pixel when the text swaps.
    if (!isLoading && itemRef.current) idleWidth.current = itemRef.current.getBoundingClientRect().width
  })

  // BEHAVIOUR: switch tabs through legacy; React renders the facade reaction.
  function handleNavClick(event: React.MouseEvent<HTMLElement>) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  return (
    <li
      ref={itemRef}
      className={[
        className,
        skin.navItem,
        isActive ? `md-sidebar__nav-item--selected ${skin.navItemSelected}` : '',
        isLoading ? `md-sidebar__nav-item--loading ${skin.navLoading}` : '',
      ].filter(Boolean).join(' ')}
      // No width on the server / first client render (idleWidth is null there),
      // so the SSR markup and the hydration render match.
      style={isLoading && idleWidth.current ? { width: idleWidth.current } : undefined}
      onClick={handleNavClick}
    >
      <div className={`md-sidebar__nav-label ${skin.navLabel}`}>
        {isLoading ? `+${loadingCount}` : label}
      </div>
    </li>
  )
}
